import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../../../ui/dialog";
import { Input } from "../../../../ui/input";
import { Button } from "../../../../ui/button";
import { Badge } from "../../../../ui/badge";
import { X } from "lucide-react";
import { GlobalVariableRegistry } from "../../utils/types";
import { PlaceholderModalState } from "../../hooks/hooks";

interface PlaceholderModalProps {
  placeholderModalState: PlaceholderModalState;
  globalVariableRegistry: GlobalVariableRegistry;
  setPlaceholderModalState: (state: PlaceholderModalState | ((prev: PlaceholderModalState) => PlaceholderModalState)) => void;
  onCancel: () => void;
  getCurrentExamples: () => string[];
  addExampleToPlaceholder: () => void;
  removeExampleFromPlaceholder: (index: number) => void;
}

const PlaceholderModal: React.FC<PlaceholderModalProps> = ({
  placeholderModalState,
  globalVariableRegistry,
  setPlaceholderModalState,
  onCancel,
  getCurrentExamples,
  addExampleToPlaceholder,
  removeExampleFromPlaceholder,
}) => {
  return (
    <Dialog open={placeholderModalState.visible} onOpenChange={onCancel}>
      <DialogContent className="sm:max-w-lg" style={{ zIndex: 9999 }}>
        <DialogHeader>
          <DialogTitle className="text-lg font-gc-med text-foreground">
            Edit Examples for {`{${placeholderModalState.currentPlaceholder}}`}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-6">
          {!placeholderModalState.isNewVariable && globalVariableRegistry[placeholderModalState.currentPlaceholder] && (
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
              <p className="text-sm font-gc-med text-blue-800">
                This is a shared variable used across the conversation tree.
              </p>
              <p className="text-sm text-blue-700 mt-1">
                Changes to examples will affect all nodes using this variable.
              </p>
            </div>
          )}
          
          <div>
            <h4 className="text-sm font-gc-med text-foreground mb-3">Add Examples</h4>
            <div className="flex gap-2">
              <Input 
                value={placeholderModalState.newExample} 
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPlaceholderModalState((prev: PlaceholderModalState) => ({ ...prev, newExample: e.target.value }))}
                onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => e.key === 'Enter' && addExampleToPlaceholder()}
                placeholder="Enter an example value"
                className="flex-1"
              />
              <Button onClick={addExampleToPlaceholder} className="bg-foreground hover:bg-foreground/90 text-background">
                Add
              </Button>
            </div>
          </div>

          <div>
            <h4 className="text-sm font-gc-med text-foreground mb-3">Examples</h4>
            <div className="max-h-[200px] overflow-auto">
              {getCurrentExamples().length === 0 ? (
                <p className="text-sm text-muted-foreground">No examples yet</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {getCurrentExamples().map((example: string, index: number) => (
                    <Badge 
                      key={`${example}-${index}`}
                      variant="secondary"
                      className="flex items-center gap-1 py-1 px-2"
                    >
                      {example}
                      <button
                        onClick={() => removeExampleFromPlaceholder(index)}
                        className="ml-1 hover:text-red-600 transition-colors"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="pt-4 border-t border-border">
            <p className="text-xs text-muted-foreground">
              These examples will be used for training and can help generate better variations of this content.
              {!placeholderModalState.isNewVariable && " Changes to this variable affect all places it's used."}
            </p>
          </div>
        </div>
        
        <DialogFooter>
          <Button onClick={onCancel} variant="outline">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PlaceholderModal; 